import { Logger } from '@/shared/logger/logger';

// Define BrowserInstanceData interface to avoid circular dependencies
export type BrowserInstanceData = {
  bd: any; // TBrowserData
  cdas: any; // TCustomerDataArrays
  ii: string;
  pids: any; // TPlatformIdentifiers
  sd: any; // TEventSessionData
  uaHeaders: any; // UaHeaders
};

/**
 * Singleton class that manages the identity state and notifies listeners of changes
 * Used to manage asynchronous updates to identity data without blocking execution
 */
export class IdentityStateManager {
  private static instance: IdentityStateManager;
  private state: BrowserInstanceData | null = null;
  private listeners: ((data: BrowserInstanceData) => void)[] = [];

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance of IdentityStateManager
   */
  static getInstance(): IdentityStateManager {
    if (!this.instance) {
      this.instance = new IdentityStateManager();
    }
    return this.instance;
  }

  /**
   * Get the current state
   */
  getState(): BrowserInstanceData | null {
    return this.state;
  }

  /**
   * Update the state and notify all listeners
   * @param data The new state data
   */
  setState(data: BrowserInstanceData): void {
    const previousState = this.state;
    this.state = data;

    // Log state changes in debug mode
    Logger.debug('Identity state updated', {
      current: {
        hasIdentityUpdate: !!data.pids,
        ii: data.ii,
      },
      previous: previousState
        ? {
            hasIdentityUpdate: !!previousState.pids,
            ii: previousState.ii,
          }
        : null,
    });

    this.notifyListeners();
  }

  /**
   * Subscribe to state changes
   * @param listener Function that will be called when state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: (data: BrowserInstanceData) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of state change
   * @private
   */
  private notifyListeners(): void {
    if (!this.state) {
      Logger.warn('Attempted to notify listeners with null state');
      return;
    }

    for (const listener of this.listeners) {
      try {
        listener(this.state);
      } catch (error) {
        Logger.error('Error in identity state listener', error);
      }
    }
  }
}
